import type { ProviderGetImage } from '@nuxt/image'
import { joinURL } from 'ufo'
import { useRuntimeConfig } from '#app'

/**
 * Extract image ID from SAPI hash
 * Based on API examples: 02417841_1, 02417841_2, etc.
 * Hash format: "https://res.cloudinary.com/snipes-stg/image/upload/f_jpg/02417841_1"
 */
const extractImageId = (hash: string): string => {
  // Look for pattern like 02417841_1, 02417841_2, etc. (8 digits + underscore + number)
  const match = hash.match(/(\d{8}_\d+)/)

  if (match) {
    return match[0] // Returns "02417841_1", "02417841_2", etc.
  }

  // Fallback: look for just 8-digit numbers and append _1
  const fallbackMatch = hash.match(/(\d{8})/)
  if (fallbackMatch) {
    return `${fallbackMatch[0]}_1`
  }

  // If no digits found, return the original hash
  return hash
}

/**
 * Build the product path from current route or product info
 * Should return something like: inhale-black-green-glare-green-39120
 * Handles both /p/product-slug and /locale/p/product-slug patterns
 */
/**
 * Extract product path from the src query parameter
 * This avoids SSR timing issues with window.location
 */
const getProductPathFromSrc = (src: string): string => {
  try {
    const url = new URL(src, 'https://example.com') // Need base for relative URLs
    const productPath = url.searchParams.get('productPath')
    return productPath ? decodeURIComponent(productPath) : ''
  } catch {
    return getProductPathFallback()
  }
}

/**
 * Fallback: Try to get product path from window.location (for zoom gallery)
 */
const getProductPathFallback = (): string => {
  if (typeof window !== 'undefined') {
    try {
      const pathname = window.location.pathname
      const pathSegments = pathname.split('/').filter(Boolean)
      const pIndex = pathSegments.findIndex((segment) => segment === 'p')
      if (pIndex !== -1 && pathSegments[pIndex + 1]) {
        const productPath = pathSegments[pIndex + 1]
        return productPath
      }
    } catch (error) {
      console.warn('Could not access route for product path:', error)
    }
  }
  return ''
}

/**
 * Get environment configuration
 */
const getEnvironmentConfig = () => {
  let env = ''
  try {
    const { public: pub } = useRuntimeConfig()
    env = String(pub.appEnv || '').toLowerCase()
  } catch {
    // Fallback to process.env if useRuntimeConfig fails
    env = String(
      process.env.NUXT_PUBLIC_APP_ENV || process.env.APP_ENV || '',
    ).toLowerCase()
  }
  return env === 'prod' || env === 'production'
}

/**
 * Calculate height based on context and aspect ratio
 */
const calculateHeight = (
  modifiers: Record<string, any>,
  isPDPContext: boolean,
  width: number,
): number => {
  if (modifiers.height) {
    return modifiers.height
  }

  if (isPDPContext) {
    // Use 3:4 aspect ratio for PDP
    return Math.round(width / 0.75)
  }

  // Default to square images (1:1 aspect ratio)
  return width
}

/**
 * Build production URL
 */
const buildProductionUrl = (
  isPDPContext: boolean,
  width: number,
  height: number,
  quality: number,
  imageId: string,
  productPath: string,
): string => {
  const operations = isPDPContext
    ? `f_auto,b_gen_fill,c_auto_pad,g_auto,w_${width},h_${height},q_${quality}`
    : `f_auto,w_${width},h_${height},q_${quality},c_limit`
  const baseUrl = 'https://asset.snipes.com/images'

  return joinURL(
    baseUrl,
    operations,
    imageId,
    productPath ? productPath : 'img',
  )
}

/**
 * Build staging URL
 */
const buildStagingUrl = (
  isPDPContext: boolean,
  width: number,
  height: number,
  quality: number,
  imageId: string,
  isCmsImage: boolean,
): string => {
  const operations = isPDPContext
    ? `f_auto,b_gen_fill,c_auto_pad,g_auto,w_${width},h_${height},q_${quality}`
    : `f_auto,w_${width},h_${height},q_${quality},c_limit`
  const baseUrl = isCmsImage
    ? 'https://asset.snipes.com/image/upload/'
    : 'https://res.cloudinary.com/snipes-stg/image/upload/'

  return isPDPContext
    ? joinURL(baseUrl, operations, `${imageId}.jpg`)
    : joinURL(baseUrl, operations, imageId)
}

export const getImage: ProviderGetImage = (
  src,
  { modifiers = {}, baseURL: _baseURL } = {},
) => {
  const isProd = getEnvironmentConfig()
  const productPath = getProductPathFromSrc(src)
  const cleanSrc = src.split('?')[0]
  const imageId = modifiers.skipIdExtraction
    ? cleanSrc
    : extractImageId(cleanSrc)

  // Extract width, height, and quality from modifiers (provided by Nuxt for srcset)
  const width = modifiers.width || (isProd ? 680 : 645)
  const quality = modifiers.quality || (isProd ? 85 : 75)

  // Check if this is PDP context based on product path pattern
  // Apply PDP context for all images with product path, including small gallery thumbnails
  const isPDPContext = Boolean(productPath && productPath.match(/-\d+$/))

  const height = calculateHeight(modifiers, isPDPContext, width)

  const url = isProd
    ? buildProductionUrl(
        isPDPContext,
        width,
        height,
        quality,
        imageId,
        productPath,
      )
    : buildStagingUrl(
        isPDPContext,
        width,
        height,
        quality,
        imageId,
        !!modifiers.skipIdExtraction,
      )

  return { url }
}
